<script setup lang="ts">
    import { useCollapsibleContext } from './context';

    defineProps<{
        /** Keep the content in the DOM while closed (hidden), so its state survives a close */
        keepMounted?: boolean;
    }>();

    const { open, contentId } = useCollapsibleContext('CollapsibleContent');
</script>

<template>
    <Transition name="ui-collapsible">
        <div
            v-if="keepMounted || open"
            v-show="open"
            :id="contentId"
            class="ui-collapsible-content"
            :data-state="open ? 'open' : 'closed'"
        >
            <div class="ui-collapsible-content__inner">
                <slot />
            </div>
        </div>
    </Transition>
</template>

<style scoped>
    @layer components {
        /*
          Height animates through a grid track, 0fr to 1fr, so the content needs no measuring.
          The inner element must be able to shrink to 0px, so padding and borders belong inside the slot, not on this element.
        */
        .ui-collapsible-content {
            display: grid;
            grid-template-rows: 1fr;
            transition:
                grid-template-rows 200ms cubic-bezier(0.4, 0, 0.2, 1),
                opacity 200ms cubic-bezier(0.4, 0, 0.2, 1);
        }

        .ui-collapsible-enter-from,
        .ui-collapsible-leave-to {
            grid-template-rows: 0fr;
            opacity: 0;
        }

        .ui-collapsible-content__inner {
            min-height: 0;
        }

        /* Clip only while animating, so focus rings and popovers in open content are not cut off */
        :is(.ui-collapsible-enter-active, .ui-collapsible-leave-active) > .ui-collapsible-content__inner {
            overflow: hidden;
        }

        @media (prefers-reduced-motion: reduce) {
            .ui-collapsible-content {
                transition: none;
            }
        }
    }
</style>
